import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { BREAKPOINT, inboxApi, useMediaQuery, type Campaign, type Template } from '@wa/shared';
import { ListsColumn } from '@/broadcast/ListsColumn';
import { BroadcastThread } from '@/broadcast/BroadcastThread';
import { MembersPanel } from '@/broadcast/MembersPanel';
import { TemplatesModal } from '@/broadcast/TemplatesModal';
import { QuickImportDialog } from '@/broadcast/QuickImportDialog';
import { type Drill } from '@/broadcast/BroadcastBubble';
import { ImportDialog } from '@/contacts/ImportDialog';
import { CampaignBuilder } from '@/campaigns/CampaignBuilder';
import { CampaignReport } from '@/campaigns/CampaignReport';
import { CampaignStatusChip } from '@/campaigns/CampaignStatusChip';

/**
 * Broadcast lists — the reference's model, replacing a table of campaigns.
 *
 * A list is the thing; a broadcast is something that happened to it. So the
 * screen is lists on the left, the chosen list's history in the middle as a
 * thread, and its members on the right, with one action: send a template. The
 * campaign engine underneath is unchanged — a send here is a campaign with the
 * list preset and the builder opened on it — so nothing about pricing, pacing,
 * scheduling or the reports moved.
 *
 * Three columns from xl, two from md with the members behind a toggle, one on a
 * phone where the list is the first screen and the thread the second.
 */
export function BroadcastPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { listId } = useParams();
  const selectedId = listId ? Number(listId) : null;

  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const triple = useMediaQuery(`(min-width: ${BREAKPOINT.xl}px)`);

  // Open by default only where it has a column of its own. Below xl the members
  // panel takes the thread's place, so "open by default" there would mean every
  // list opening on its members instead of on what was sent to them.
  const [membersToggled, setMembersToggled] = useState<boolean | null>(null);
  const membersOpen = membersToggled ?? triple;
  const setMembersOpen = (next: boolean | ((v: boolean) => boolean)) =>
    setMembersToggled(typeof next === 'function' ? next(membersOpen) : next);

  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });
  const fields = useQuery({ queryKey: ['contact-fields'], queryFn: inboxApi.contactFields });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels });
  const selected = (segments.data ?? []).find((s) => s.id === selectedId) ?? null;

  const thread = useQuery({
    queryKey: ['broadcasts', selectedId],
    queryFn: () => inboxApi.broadcastsTo(selectedId!),
    enabled: selectedId !== null,
    refetchInterval: 15_000,
  });

  // A wide screen with nothing chosen opens the newest list, the way the
  // reference does; a phone shows the list of lists and waits.
  useEffect(() => {
    if (wide && selectedId === null && segments.data && segments.data.length > 0) {
      const newest = [...segments.data].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]!;
      navigate(`/broadcasts/${newest.id}`, { replace: true });
    }
  }, [wide, selectedId, segments.data, navigate]);

  const [showTemplates, setShowTemplates] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [newList, setNewList] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [report, setReport] = useState<{ campaign: Campaign; drill: Drill } | null>(null);

  // Send = a draft campaign on this list with the chosen template, then the
  // builder for values and timing. Two requests rather than a bespoke "send now"
  // endpoint, so a broadcast is a campaign in every report that already exists.
  const startSend = useMutation({
    mutationFn: async (template: Template) => {
      const channel = (channels.data ?? [])[0];
      if (!channel) throw new Error('No active number to send from.');
      return inboxApi.createCampaign({
        name: template.name,
        templateId: template.id,
        channelId: channel.id,
        segmentId: selectedId,
        variableMap: [],
      });
    },
    onSuccess: (created) => {
      setShowTemplates(false);
      void queryClient.invalidateQueries({ queryKey: ['broadcasts', selectedId] });
      setDraftId(created.id);
    },
  });

  const createList = useMutation({
    mutationFn: (name: string) => inboxApi.createSegment({ name, kind: 0 }),
    onSuccess: (made) => {
      setNewList(false);
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      navigate(`/broadcasts/${made.id}`);
    },
  });

  const draft = useQuery({
    queryKey: ['campaign', draftId],
    queryFn: () => inboxApi.campaign(draftId!),
    enabled: draftId !== null,
  });
  const reportDetail = useQuery({
    queryKey: ['campaign', report?.campaign.id],
    queryFn: () => inboxApi.campaign(report!.campaign.id),
    enabled: report !== null,
  });

  const sampleFile = () => {
    const csv = 'phone,name\n919876543210,Asha Menon\n919876543211,Rohit Nair\n';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'broadcast-list-sample.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const closeDraft = () => {
    setDraftId(null);
    void queryClient.invalidateQueries({ queryKey: ['broadcasts', selectedId] });
  };

  // ------------------------------------------------------------------ panels --

  const lists = (
    <ListsColumn
      segments={segments.data ?? []}
      selectedId={selectedId}
      onSelect={(id) => navigate(`/broadcasts/${id}`)}
      onNewList={() => setNewList(true)}
      onExcelImport={() => setShowImport(true)}
      onSampleFile={sampleFile}
      onQuickImport={() => { if (selected) setShowQuick(true); else setNewList(true); }}
      isLoading={segments.isPending}
    />
  );

  const threadPane = selected ? (
    <BroadcastThread
      segment={selected}
      campaigns={thread.data ?? []}
      isLoading={thread.isPending}
      membersOpen={membersOpen}
      onToggleMembers={() => setMembersOpen((v) => !v)}
      onImport={() => setShowImport(true)}
      onQuickImport={() => setShowQuick(true)}
      onSampleFile={sampleFile}
      onSend={() => setShowTemplates(true)}
      onDrill={(campaign, drill) => (campaign.status === 0 ? setDraftId(campaign.id) : setReport({ campaign, drill }))}
      onBack={wide ? undefined : () => navigate('/broadcasts')}
    />
  ) : (
    <div className="grid h-full place-items-center bg-(--color-surface) text-body text-(--color-muted)">
      {segments.data && segments.data.length === 0 ? 'Make a list to start.' : 'Pick a list.'}
    </div>
  );

  const members = selected ? <MembersPanel segment={selected} onClose={triple ? undefined : () => setMembersOpen(false)} /> : null;

  // The draft builder and the report take the thread's place rather than opening
  // a modal over it: both are tall, and the list column staying put is what says
  // "you are still here, in this list".
  const overlay = draftId !== null
    ? (draft.data
        ? <Panel title={draft.data.campaign.name} chip={<CampaignStatusChip campaign={draft.data.campaign} />} onClose={closeDraft}>
            {draft.data.campaign.status === 0
              ? <CampaignBuilder campaign={draft.data.campaign} onClose={closeDraft} locked={{ listName: selected?.name ?? 'this list' }} />
              : <CampaignReport campaign={draft.data.campaign} onClose={closeDraft} />}
          </Panel>
        : <Panel title="Loading…" onClose={closeDraft}><p className="p-5 text-small text-(--color-muted)">Loading…</p></Panel>)
    : report !== null && reportDetail.data
      ? <Panel title={report.campaign.templateName} chip={<CampaignStatusChip campaign={reportDetail.data.campaign} />} onClose={() => setReport(null)}>
          <CampaignReport campaign={reportDetail.data.campaign} onClose={() => setReport(null)} />
        </Panel>
      : null;

  const middle = overlay ?? threadPane;

  return (
    <>
      {!wide ? (
        <div className="h-full min-h-0">
          {selectedId === null ? lists : (membersOpen && members && !overlay ? members : middle)}
        </div>
      ) : (
        <div className={[
          'grid h-full min-h-0',
          triple && membersOpen && members && !overlay
            ? 'grid-cols-[300px_minmax(0,1fr)_280px]'
            : 'grid-cols-[300px_minmax(0,1fr)]',
        ].join(' ')}>
          <div className="min-h-0">{lists}</div>
          <div className="min-h-0">{!triple && membersOpen && members && !overlay ? members : middle}</div>
          {triple && membersOpen && members && !overlay && <div className="min-h-0">{members}</div>}
        </div>
      )}

      <TemplatesModal open={showTemplates} onClose={() => setShowTemplates(false)} onPick={(t) => startSend.mutate(t)} isPicking={startSend.isPending} />
      {showImport && (
        <ImportDialog
          fields={fields.data ?? []}
          segments={segments.data ?? []}
          defaultSegmentId={selectedId}
          onClose={() => setShowImport(false)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['segments'] });
            void queryClient.invalidateQueries({ queryKey: ['segment-members', selectedId] });
          }}
        />
      )}
      {showQuick && selectedId !== null && <QuickImportDialog segmentId={selectedId} onClose={() => setShowQuick(false)} />}
      {newList && <NewListDialog busy={createList.isPending} onClose={() => setNewList(false)} onCreate={(name) => createList.mutate(name)} />}
    </>
  );
}

function Panel({ title, chip, onClose, children }: { title: string; chip?: React.ReactNode; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-(--color-surface)">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-3">
        <button type="button" onClick={onClose} aria-label="Back to the list" className="tap -ml-1 grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
          <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <h2 className="min-w-0 flex-1 truncate text-body font-semibold">{title}</h2>
        {chip}
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

function NewListDialog({ busy, onClose, onCreate }: { busy: boolean; onClose: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <form
        role="dialog" aria-modal="true" aria-label="New list"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) onCreate(name.trim()); }}
        className="w-full max-w-sm rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)"
      >
        <h2 className="text-lead font-semibold">New list</h2>
        <label className="mt-3 block">
          <span className="text-small font-medium text-(--color-muted)">List name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Diwali customers"
            className="mt-1 h-10 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 text-body outline-none focus:border-(--color-brand)"
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
          <button type="submit" disabled={!name.trim() || busy} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60">
            Create list
          </button>
        </div>
      </form>
    </div>
  );
}
